import { useFeatureFlags } from '@harnessio/ff-react-client-sdk';
import {
  Toast,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS
} from 'cdk-radial';
import { IAMUser, useDMSAuth } from 'drive-ui-packages';
import _ from 'lodash';
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { APP_ENV } from 'adapters/constants';
import {
  Client,
  CustomClientValues,
  HeaderData,
  UserDepartmentDetails
} from 'adapters/types';
import {
  DepartmentIds,
  DepartmentType,
  ExtendedAccount,
  ExtendedAccountInfoType
} from 'adapters/types/Client';
import { areHeadersDifferent } from 'adapters/utils';
import { setHarnessFlags, setJWTFlag } from 'pages/LandingPage/slice';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { setErrorMessages } from './ErrorMessageHandler/slice';
import { useHarnessFlagClient } from './HarnessProvider';
import {
  getSelectedStoreOption,
  setNoStoreAvailable,
  setSelectedStoreOption
} from './StoreSelect/slice';
import { getDropDownOptions } from './StoreSelect/utils';

type ContextValue = Client | null;

const ClientContext = createContext<ContextValue>(null);

export function ClientProvider({
  client,
  children,
  jwtFlag
}: {
  client: Client;
  children: React.ReactNode;
  jwtFlag: boolean;
}): React.JSX.Element {
  const [authorizedClient, setAuthorizedClient] = useState<Client>(client);
  const [headerData, setHeaderData] = useState<HeaderData>(client.headers);
  const [showSoldStoreMessage, setShowSoldStoreMessage] = useState(false);
  const dispatch = useAppDispatch();
  const [allowSoldStoresFlag, setAllowSoldStoresFlag] = useState<
    boolean | undefined
  >();

  const flagOffValue = allowSoldStoresFlag === undefined ? null : undefined;

  const { authState, dmsAuthSession } = useDMSAuth(
    APP_ENV,
    allowSoldStoresFlag ? ['OPEN', 'SOLD'] : flagOffValue,
    jwtFlag
  );
  const accessToken = authState?.accessToken?.accessToken;
  const selectedStoreOption = useAppSelector(getSelectedStoreOption);
  const harnessFlags = useFeatureFlags();
  const intl = useIntl();
  const harnessClient = useHarnessFlagClient();
  const iamUser = dmsAuthSession.oktaUser?.iamUser as IAMUser | undefined;

  const departmentIds = useMemo<DepartmentIds[] | null>(
    () =>
      dmsAuthSession.oktaUser?.extendedAccountInfo?.flatMap(
        (account: ExtendedAccount) => ({
          storeId: account.storeId,
          departmentIds: account?.departmentIds ? account?.departmentIds[0] : ''
        })
      ) ?? null,
    [dmsAuthSession.oktaUser?.extendedAccountInfo]
  );

  const departments = useMemo<UserDepartmentDetails[]>(
    () =>
      dmsAuthSession.oktaUser?.extendedAccountInfo?.reduce(
        (filtered: DepartmentType, data: ExtendedAccountInfoType) => {
          if (
            data.accounts &&
            data.companyId &&
            data.departmentIds &&
            data.accounts[0] !== '' &&
            data.departmentIds[0] !== ''
          ) {
            filtered.push({
              storeId: data.storeId,
              companyId: data.companyId,
              departmentName: data.accounts[0],
              storeStatus: data.storeStatus
            });
          }
          return filtered;
        },
        []
      ) ?? [],
    [dmsAuthSession.oktaUser?.extendedAccountInfo]
  );

  const roles = useMemo(
    () => iamUser?.assignments[0].roles.map(role => role.code) ?? [],
    [iamUser?.assignments]
  );

  const defaultStore = useMemo(
    () =>
      iamUser?.assignments[0].stores.find(store => store.defaultStore) ??
      iamUser?.assignments[0].stores[0] ??
      null,
    [iamUser?.assignments]
  );

  const customClientValues = useMemo<CustomClientValues>(
    () => ({
      newSession: true,
      permissions: iamUser?.authorities ?? [],
      stores: iamUser?.assignments[0].stores ?? [],
      defaultStore,
      roles,
      departmentIds,
      departments
    }),
    [
      defaultStore,
      departmentIds,
      departments,
      iamUser?.assignments,
      iamUser?.authorities,
      roles
    ]
  );

  useEffect(() => {
    const getDefaultStoreId =
      iamUser?.assignments[0].stores.find(store => store.defaultStore) ||
      iamUser?.assignments[0].stores[0];

    if (
      iamUser &&
      iamUser?.enterprise?.id !== '' &&
      getDefaultStoreId?.storeId
    ) {
      harnessClient?.setHarnessAttributes(
        (prev: { [key: string]: string }) => ({
          ...prev,
          enterprise: iamUser.enterprise?.id,
          store: getDefaultStoreId.storeId
        })
      );
    }
  }, [harnessClient, iamUser, iamUser?.enterprise?.id]);

  useEffect(() => {
    if (harnessFlags) {
      dispatch(setHarnessFlags(harnessFlags));
      setAllowSoldStoresFlag(
        harnessFlags.dms_accounting_allow_sold_stores_in_user_details
      );
      dispatch(setJWTFlag(jwtFlag));
    }
  }, [dispatch, harnessFlags, headerData, jwtFlag]);

  useEffect(() => {
    if (accessToken && `Bearer ${accessToken}` !== headerData.authorization) {
      setHeaderData(prevHeaderData => ({
        ...prevHeaderData,
        authorization: `Bearer ${accessToken}`
      }));
    }
  }, [accessToken, headerData.authorization]);

  useEffect(() => {
    if (iamUser) {
      setHeaderData(prevHeaderData => ({
        ...prevHeaderData,
        'remote-user': iamUser.username,
        'x-enterprise-id': iamUser.enterprise.id
      }));
      if (iamUser.assignments[0]?.stores?.length === 0) {
        dispatch(setNoStoreAvailable(true));
      }
    }
  }, [dispatch, iamUser]);

  useEffect(() => {
    const selectedStoreValue = selectedStoreOption.value;
    if (selectedStoreValue === '' && defaultStore) {
      const [option] = getDropDownOptions([defaultStore], departments);
      dispatch(setSelectedStoreOption(option));
    }
    if (selectedStoreValue !== '') {
      const departmentInfo = departments.find(
        dept => dept.storeId === selectedStoreValue
      );
      if (iamUser && !departmentInfo) {
        dispatch(
          setErrorMessages([
            intl.formatMessage({
              description: 'Error message for department name not found.',
              defaultMessage:
                'This store is missing a department, company, or store ID. Please contact CDK Support to verify store data.'
            })
          ])
        );
      }

      if (iamUser && !departmentInfo?.departmentName) {
        dispatch(
          setErrorMessages([
            intl.formatMessage({
              description: 'Error message for department name not found.',
              defaultMessage:
                'Department information not found. Please retry in a few moments. If issue persists, please contact CDK Support.'
            })
          ])
        );
      }
      if (departmentInfo?.storeStatus === 'SOLD') {
        setShowSoldStoreMessage(true);

        if (!departmentInfo?.companyId) {
          dispatch(
            setErrorMessages([
              intl.formatMessage({
                description: 'Error message for companyId not found.',
                defaultMessage:
                  'This store is missing a company ID. Please contact CDK Support to verify store data.'
              })
            ])
          );
        }
        if (!departmentInfo?.departmentName) {
          dispatch(
            setErrorMessages([
              intl.formatMessage({
                description: 'Error message for companyId not found.',
                defaultMessage:
                  'This store is missing a department name. Please contact CDK Support to verify store data.'
              })
            ])
          );
        }
      }
      setHeaderData(prevHeaderData => ({
        ...prevHeaderData,
        'x-department-name': departmentInfo?.departmentName,
        'x-store-id': selectedStoreOption.value
      }));
    }
  }, [
    defaultStore,
    departments,
    dispatch,
    iamUser,
    intl,
    selectedStoreOption.value
  ]);

  useEffect(() => {
    if (areHeadersDifferent(authorizedClient.headers, headerData)) {
      setAuthorizedClient(prevClient => ({
        ...prevClient,
        headers: headerData,
        queryResult: dmsAuthSession?.queryResult
      }));
    }
  }, [authorizedClient.headers, headerData, dmsAuthSession?.queryResult]);

  const clientContextValue = useMemo(
    () => ({ ...authorizedClient, customClientValues }),
    [authorizedClient, customClientValues]
  );

  return (
    <>
      <ClientContext.Provider value={clientContextValue}>
        {children}
      </ClientContext.Provider>
      {showSoldStoreMessage ? (
        <Toast
          actions={[
            {
              onClick: () => setShowSoldStoreMessage(false),
              text: intl.formatMessage({
                description: 'dismiss button label',
                defaultMessage: 'Dismiss'
              }),
              dataTestId: 'dismiss-btn'
            }
          ]}
          data-testid="toast-neutral"
          position={TOAST_POSITIONS.FIXED}
          variant={TOAST_VARIANTS.NEUTRAL}
          content={intl.formatMessage({
            description: 'Content for sold store info toast',
            defaultMessage: 'This store is sold.'
          })}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SEVEN_SECONDS}
          onVisibilityDurationEnd={() => {
            setShowSoldStoreMessage(false);
          }}
          id="toast-neutral"
        />
      ) : null}
    </>
  );
}

export const useClient = (): ContextValue => useContext(ClientContext);
